import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { notification } from 'antd';
import { FormattedMessage } from 'react-intl';
import Pficon from 'components/Pficon';
import { fetchDesignOfExperiment } from 'containers/QuestionnairePage/actions';
import { downloadExperimentalDesign } from 'containers/QuestionnairePage/model';
import { getFileNameFromResponseHeaders } from 'containers/App/utils';
import { getNodeByRefId } from 'containers/QuestionnairePage/utils';
import {
  selectQuestionnaireQuestions,
  makeSelectIsFetchingConjointSettings,
  makeSelectIsDoeInvalidated,
} from 'containers/QuestionnairePage/selectors';
import questionnaireMessages from 'containers/QuestionnairePage/messages';

export const GENERATE_DESIGN_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
};

function showDesignErrorToast({ titleMessage, descriptionMessage }) {
  notification.open({
    closeIcon: <Pficon type="close" />,
    className: 'text-size-primary',
    message: (
      <div className="color-red-6 text-size-primary">
        <Pficon type="danger" className="vertical-align-top margin-right-xxs" />
        <FormattedMessage {...titleMessage} />
      </div>
    ),
    description: descriptionMessage ? (
      <FormattedMessage {...descriptionMessage} />
    ) : null,
  });
}

export default function useMaxDiffExperimentalDesign({
  surveyId,
  refId,
  enabled = true,
}) {
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);

  const isFetchingDesign = useSelector(makeSelectIsFetchingConjointSettings);
  const isDoeInvalidated = useSelector(makeSelectIsDoeInvalidated);

  const lastSuccessDoeVersion = useSelector((state) => {
    if (!enabled) {
      return -1;
    }

    const nodes = selectQuestionnaireQuestions(state);
    const node = getNodeByRefId(nodes, refId);

    return get(node, 'properties.lastSuccessDoeVersion', -1);
  });

  const isDesignStale = lastSuccessDoeVersion >= 0 && isDoeInvalidated;

  const generateStatus = useMemo(() => {
    if (!enabled) {
      return GENERATE_DESIGN_STATUS.IDLE;
    }

    if (isFetchingDesign) {
      return GENERATE_DESIGN_STATUS.LOADING;
    }

    if (lastSuccessDoeVersion >= 0 && !isDoeInvalidated) {
      return GENERATE_DESIGN_STATUS.SUCCESS;
    }

    return GENERATE_DESIGN_STATUS.IDLE;
  }, [enabled, isFetchingDesign, isDoeInvalidated, lastSuccessDoeVersion]);

  const generateDesign = useCallback(() => {
    if (generateStatus === GENERATE_DESIGN_STATUS.LOADING) {
      return;
    }

    dispatch(fetchDesignOfExperiment());
  }, [dispatch, generateStatus]);

  const downloadDesign = useCallback(async () => {
    if (
      isDownloading ||
      isDoeInvalidated ||
      lastSuccessDoeVersion < 0 ||
      generateStatus !== GENERATE_DESIGN_STATUS.SUCCESS
    ) {
      return;
    }

    setIsDownloading(true);

    try {
      const fileResponse = await downloadExperimentalDesign(surveyId);

      if (!fileResponse.blob) {
        throw new Error('Missing design file');
      }

      const blobUrl = window.URL.createObjectURL(fileResponse.blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = getFileNameFromResponseHeaders(fileResponse.headers);
      link.download = filename || `maxdiff-design-${surveyId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showDesignErrorToast({
        titleMessage:
          questionnaireMessages.questionAnswerDropdown.downloadDesignFail,
        descriptionMessage:
          questionnaireMessages.questionAnswerDropdown.downloadDesignFailDesc,
      });
    } finally {
      setIsDownloading(false);
    }
  }, [
    generateStatus,
    isDoeInvalidated,
    isDownloading,
    lastSuccessDoeVersion,
    surveyId,
  ]);

  return {
    generateDesign,
    downloadDesign,
    generateStatus,
    isDownloading,
    isDesignStale,
    isDownloadEnabled:
      lastSuccessDoeVersion >= 0 &&
      !isDoeInvalidated &&
      generateStatus === GENERATE_DESIGN_STATUS.SUCCESS,
    showStaleIndicator: isDesignStale,
  };
}
